import React from 'react';
import { Inbox, Thermometer, MapPin, User, Clock } from 'lucide-react';

/**
 * AuditTrailList — read-only chronological audit log.
 * Each entry rendered as a compact row with timestamp, event, user, location, and temperature.
 */
const AuditTrailList = ({ entries = [] }) => {
  if (!entries || entries.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 sm:p-8 text-center">
        <Inbox className="w-7 h-7 text-slate-400 mx-auto mb-3" />
        <p className="text-sm sm:text-base text-slate-600">No audit events available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
      {entries.map((e) => (
        <div
          key={e.id || `${e.timestamp}-${e.event}`}
          data-testid={`audit-${e.id || e.timestamp}`}
          className="p-3 sm:p-4"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-semibold text-slate-900">{e.event}</p>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] sm:text-xs text-slate-600">
                <Span icon={Clock}>{formatTs(e.timestamp)}</Span>
                {e.user && <Span icon={User}>{e.user}</Span>}
                {e.location && <Span icon={MapPin}>{e.location}</Span>}
                {typeof e.temperature_celsius === 'number' && (
                  <Span icon={Thermometer}>
                    {e.temperature_celsius.toFixed(2)}°C
                  </Span>
                )}
              </div>
              {e.notes && (
                <p className="text-xs sm:text-sm text-slate-700 mt-1.5 italic break-words">
                  {e.notes}
                </p>
              )}
            </div>
            {e.product_id && (
              <span className="text-[10px] sm:text-[11px] font-mono text-slate-500 flex-shrink-0">
                {e.product_id}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const Span = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1">
    <Icon className="w-3 h-3 text-slate-400" />
    {children}
  </span>
);

const formatTs = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default AuditTrailList;
